// src/components/Schedule.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  deleteDoc,
  doc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  useDroppable,
  useDraggable,
} from "@dnd-kit/core";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Calendar,
  CalendarDays,
  X,
  Briefcase,
  User,
  CheckCircle,
  ExternalLink,
  AlertCircle,
  Clock,
  Activity,
  GripVertical,
  Search,
  Focus,
} from "lucide-react";
import { useTheme } from "../ThemeContext";
import { evaluateTasks } from "../utils/status";
import { usePermissions } from "../permissions/PermissionsProvider";
import { ACTIONS } from "../permissions/definition";
import HeaderActionsMinimal from "./HeaderActionsMinimal";

const ANALYSTS = ["Zak", "Omar", "Muhab", "Aseel", "Mustafa", "Sadegh"];

const getDaysInMonth = (year, month) => {
  const date = new Date(year, month, 1);
  const days = [];
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};

const getTodayCentricDays = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = [];
  // Start from 3 days before today, total 30 days
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() - 3);
  for (let i = 0; i < 30; i++) {
    const day = new Date(startDate);
    day.setDate(startDate.getDate() + i);
    days.push(day);
  }
  return days;
};

const formatDateKey = (date) => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
};

const getStatusBgColor = (status) => {
  switch (status) {
    case "Completed":
      return "bg-green-500";
    case "Overdue":
      return "bg-red-500";
    case "Attention":
      return "bg-amber-500";
    case "In Progress":
      return "bg-blue-500";
    default:
      return "bg-gray-400";
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "Completed":
      return "bg-green-100 text-green-800 border-green-200";
    case "Overdue":
      return "bg-red-100 text-red-800 border-red-200";
    case "Attention":
      return "bg-amber-100 text-amber-700 border-amber-200";
    case "In Progress":
      return "bg-blue-100 text-blue-800 border-blue-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case "Completed":
      return <CheckCircle className="w-4 h-4" />;
    case "Overdue":
      return <AlertCircle className="w-4 h-4" />;
    case "Attention":
      return <Clock className="w-4 h-4" />;
    case "In Progress":
      return <Activity className="w-4 h-4" />;
    default:
      return <Activity className="w-4 h-4" />;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const circleFlagCodeMap = {
  UK: "gb",
  "UNITED KINGDOM": "gb",
  USA: "us",
  "UNITED STATES": "us",
  GERMANY: "de",
  FRANCE: "fr",
  JAPAN: "jp",
  CHINA: "cn",
  INDIA: "in",
  BRAZIL: "br",
  CANADA: "ca",
  AUSTRALIA: "au",
  ITALY: "it",
  SPAIN: "es",
  RUSSIA: "ru",
  "SOUTH KOREA": "kr",
  NETHERLANDS: "nl",
  TURKEY: "tr",
  "SAUDI ARABIA": "sa",
  SWITZERLAND: "ch",
  SWEDEN: "se",
  POLAND: "pl",
  BELGIUM: "be",
  NORWAY: "no",
  AUSTRIA: "at",
  DENMARK: "dk",
  FINLAND: "fi",
  IRELAND: "ie",
  "NEW ZEALAND": "nz",
  SINGAPORE: "sg",
  UAE: "ae",
  "UNITED ARAB EMIRATES": "ae",
  "UAE - ABU DHABI": "ae",
  "UAE - AJMAN": "ae",
  "UAE - DUBAI": "ae",
  "UAE - FUJAIRAH": "ae",
  "UAE - RAK": "ae",
  "UAE - SHARJAH": "ae",
  "UAE - UAQ": "ae",
  ISRAEL: "il",
  "SOUTH AFRICA": "za",
  MEXICO: "mx",
  EGYPT: "eg",
  MOROCCO: "ma",
  ALGERIA: "dz",
  TUNISIA: "tn",
  LIBYA: "ly",
  QATAR: "qa",
  KUWAIT: "kw",
  BAHRAIN: "bh",
  OMAN: "om",
  JORDAN: "jo",
  LEBANON: "lb",
  IRAQ: "iq",
  YEMEN: "ye",
  SYRIA: "sy",
  PALESTINE: "ps",
  IRAN: "ir",
  PAKISTAN: "pk",
  AFGHANISTAN: "af",
  DJIBOUTI: "dj",
  MAURITANIA: "mr",
  SOMALIA: "so",
  SUDAN: "sd",
};

const getCircleFlagCode = (countryOrCompany) => {
  if (!countryOrCompany) return "xx";
  const normalized = countryOrCompany.trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(normalized)) {
    return normalized.toLowerCase();
  }
  return circleFlagCodeMap[normalized] || "xx";
};

const getCircleFlagUrl = (countryOrCompany) =>
  `https://hatscripts.github.io/circle-flags/flags/${getCircleFlagCode(
    countryOrCompany
  )}.svg`;

const CountryFlagIcon = ({ country, size = 18, className = "" }) => (
  <img
    src={getCircleFlagUrl(country)}
    alt={country ? `${country} flag` : "Flag"}
    width={size}
    height={size}
    className={`flex-shrink-0 ${className}`}
    style={{
      borderRadius: "50%",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
      border: "1px solid rgba(15, 23, 42, 0.08)",
      backgroundColor: "#fff",
    }}
    loading="lazy"
  />
);

// Draggable Task Pill for Task Pool
const DraggableTaskPill = ({ task, onClick, draggable = true }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `task-${task.projectId}-${task.taskNo}`,
      data: {
        type: "task",
        task,
      },
      disabled: !draggable,
    });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  const statusColor = getStatusBgColor(task.taskStatus);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-2 rounded-lg border border-gray-200 bg-white hover:border-gray-300 transition-all ${
        draggable ? "cursor-grab" : "cursor-default"
      } ${isDragging ? "opacity-50 shadow-lg" : ""}`}
      {...(draggable ? { ...attributes, ...listeners } : {})}
    >
      <div className="text-gray-400">
        <GripVertical className="w-4 h-4" />
      </div>
      <div className={`w-2 h-2 rounded-full ${statusColor}`} />
      <div
        className="flex-1 min-w-0"
        onClick={(e) => {
          e.stopPropagation();
          onClick(task);
        }}
      >
        <div className="text-xs font-medium text-gray-900 truncate">
          {task.projectRef}
        </div>
        <div className="text-[10px] text-gray-500 flex items-center gap-1">
          <span className="truncate flex-1 min-w-0">{task.subject}</span>
          <span className="text-gray-400 flex-shrink-0 font-bold text-sm leading-none">
            •
          </span>
          <span className="truncate flex-1 min-w-0">{task.client}</span>
        </div>
      </div>
      <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
        {task.task}
      </span>
    </div>
  );
};

// Draggable Allocation Pill (in grid cell)
const DraggableAllocationPill = ({
  allocation,
  task,
  onDelete,
  onClick,
  canManage,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `allocation-${allocation.id}`,
      data: {
        type: "allocation",
        allocation,
        task,
      },
      disabled: !canManage,
    });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  const statusColor = task ? getStatusBgColor(task.taskStatus) : "bg-gray-400";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-1 ${statusColor} text-white px-2 py-0.5 rounded ${
        canManage ? "cursor-grab" : "cursor-default"
      } max-w-[150px] overflow-hidden ${
        isDragging ? "opacity-50 shadow-lg" : ""
      }`}
      {...(canManage ? { ...attributes, ...listeners } : {})}
    >
      <div
        className="flex-1 min-w-0 cursor-pointer leading-none"
        onClick={(e) => {
          e.stopPropagation();
          onClick(task);
        }}
      >
        <div className="text-[10px] truncate">
          {task?.projectRef || "?"} ({task?.task || "?"})
        </div>
        <div className="text-[8px] opacity-80 flex items-center">
          <span className="truncate flex-1 min-w-0">
            {task?.subject || "?"}
          </span>
          <span className="flex-shrink-0 mx-0.5">•</span>
          <span className="truncate flex-1 min-w-0">{task?.client || "?"}</span>
        </div>
      </div>
      {canManage && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(allocation.id);
          }}
          className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-white/20 rounded transition-opacity flex-shrink-0"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

// Orphaned Allocation Pill (for completed/deleted/removed tasks)
const OrphanedAllocationPill = ({
  allocation,
  statusInfo,
  onDelete,
  onClick,
  canManage,
}) => {
  const bgColor = statusInfo?.color || "bg-gray-400";
  const label = statusInfo?.label || "Checking...";
  const isCompleted = statusInfo?.status === "completed";
  const task = statusInfo?.task;

  return (
    <div
      className={`group relative flex items-center gap-1 ${bgColor} text-white px-2 py-0.5 rounded max-w-[150px] overflow-hidden ${
        isCompleted ? "opacity-80 cursor-pointer" : "opacity-60 cursor-default"
      }`}
    >
      {/* Completed checkmark at top right */}
      {isCompleted && (
        <CheckCircle className="absolute top-0.5 right-0.5 w-3 h-3 text-white drop-shadow-sm" />
      )}

      <div
        className="flex-1 min-w-0 leading-none"
        onClick={(e) => {
          if (isCompleted && task && onClick) {
            e.stopPropagation();
            onClick(task);
          }
        }}
      >
        {isCompleted && task ? (
          <>
            <div className="text-[10px] truncate">
              {task.projectRef} ({task.task || "?"})
            </div>
            <div className="text-[8px] opacity-80 flex items-center">
              <span className="truncate flex-1 min-w-0">
                {task.subject || "?"}
              </span>
              <span className="flex-shrink-0 mx-0.5">•</span>
              <span className="truncate flex-1 min-w-0">
                {task.client || "?"}
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="text-[10px] truncate">{label}</div>
            <div className="text-[8px] opacity-80">
              {statusInfo?.status === "project_deleted"
                ? "Project no longer exists"
                : statusInfo?.status === "task_removed"
                ? "Task was removed"
                : statusInfo?.status === "completed"
                ? "Loading task data..."
                : "Unable to load task"}
            </div>
          </>
        )}
      </div>
      {canManage && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(allocation.id);
          }}
          className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-white/20 rounded transition-opacity flex-shrink-0"
          title="Remove this allocation"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

// Droppable Cell
const DroppableCell = ({ id, children, isOver, disabled, isHighlighted }) => {
  const { setNodeRef } = useDroppable({ id, disabled });

  return (
    <td
      ref={setNodeRef}
      className={`border-b border-r border-gray-200 px-1.5 py-1 align-top min-h-[60px] transition-colors ${
        isOver && !disabled
          ? "bg-indigo-100"
          : isHighlighted
          ? "bg-indigo-50/70"
          : ""
      }`}
    >
      {children}
    </td>
  );
};

// Task Preview Modal
const TaskPreviewModal = ({
  task,
  onClose,
  onViewProject,
  canViewFinancials,
  theme,
}) => {
  if (!task) return null;

  const isOverdue = task.taskStatus === "Overdue";
  const isAttention = task.taskStatus === "Attention";

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-start justify-center p-4 pt-16 overflow-y-auto"
      onClick={onClose}
    >
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1100]" />
      <div
        className="relative z-[1101] bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Combined Header */}
        <div
          className="sticky top-0 z-10 px-6 py-4 relative overflow-hidden"
          style={{
            background:
              theme?.loginBackground ||
              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full mix-blend-overlay filter blur-2xl opacity-20 -translate-y-1/2 translate-x-1/2"></div>

          <div className="relative z-10 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              {/* Task badges row */}
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 text-xs font-semibold bg-white/20 text-white rounded">
                  #{task.taskNo}
                </span>
                <span className="px-2 py-0.5 text-xs font-medium bg-white/20 text-white rounded">
                  {task.task}
                </span>
                <span
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    isOverdue
                      ? "bg-red-100 text-red-800"
                      : isAttention
                      ? "bg-amber-100 text-amber-800"
                      : task.taskStatus === "Completed"
                      ? "bg-green-100 text-green-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {getStatusIcon(task.taskStatus)}
                  {task.taskStatus}
                </span>
              </div>

              {/* Subject */}
              <h2 className="text-lg font-bold text-white mb-1">
                {task.subject}
              </h2>

              {/* Project ref, client, due date */}
              <div className="flex items-center gap-2 text-sm text-white/80">
                <span className="font-medium">{task.projectRef}</span>
                <span className="text-white/50">•</span>
                <span>{task.client}</span>
                <span className="text-white/50">•</span>
                <span>Due: {formatDate(task.dueDate)}</span>
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5" />
                Project Info
              </h4>
              <div className="space-y-2 bg-gray-50 rounded-lg p-3 border border-gray-100">
                <div className="flex justify-between items-center gap-3">
                  <span className="text-xs text-gray-500">Client</span>
                  <span className="text-sm font-medium text-gray-900 truncate max-w-[180px]">
                    {task.client || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between items-center gap-3">
                  <span className="text-xs text-gray-500">PoC</span>
                  <span className="text-sm font-medium text-gray-900 truncate max-w-[180px]">
                    {task.poc || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-gray-500">Country</span>
                  <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                    <CountryFlagIcon country={task.country} size={18} />
                    {task.country || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" />
                Team & Schedule
              </h4>
              <div className="space-y-2 bg-gray-50 rounded-lg p-3 border border-gray-100">
                <div className="flex justify-between">
                  <span className="text-xs text-gray-500">Analyst 1</span>
                  <span className="text-sm font-medium text-gray-900">
                    {task.analyst1 || "N/A"}
                  </span>
                </div>
                {task.analyst2 && (
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-500">Analyst 2</span>
                    <span className="text-sm font-medium text-gray-900">
                      {task.analyst2}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-xs text-gray-500">Due Date</span>
                  <span
                    className={`text-sm font-medium ${
                      task.taskStatus === "Overdue"
                        ? "text-red-600"
                        : task.taskStatus === "Attention"
                        ? "text-yellow-600"
                        : "text-gray-900"
                    }`}
                  >
                    {formatDate(task.dueDate)}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" />
                Completion
              </h4>
              <div className="space-y-2 bg-gray-50 rounded-lg p-3 border border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">Completed On</span>
                  <span
                    className={`text-sm font-medium ${
                      task.completedOn ? "text-green-600" : "text-gray-400"
                    }`}
                  >
                    {task.completedOn
                      ? formatDate(task.completedOn)
                      : "Not completed"}
                  </span>
                </div>
                {canViewFinancials && (
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Fee</span>
                      <span className="text-sm font-medium text-gray-900">
                        £{task.income || "0"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">
                        Total Inc VAT
                      </span>
                      <span className="text-sm font-bold text-indigo-600">
                        £{task.amountIncVat || "0"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 px-6 py-4 flex justify-end">
          <button
            onClick={onViewProject}
            className="h-9 px-4 text-sm font-medium text-white rounded-lg inline-flex items-center gap-1.5 transition-colors"
            style={{
              backgroundColor: theme?.primaryButtonBg || "#4f46e5",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                theme?.primaryButtonHover || "#4338ca")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                theme?.primaryButtonBg || "#4f46e5")
            }
          >
            <ExternalLink className="w-3.5 h-3.5" />
            View Project
          </button>
        </div>
      </div>
    </div>
  );
};

// Due Date Badge Component
const DueDateBadge = ({ count, onClick }) => {
  if (!count || count === 0) return null;

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="absolute top-0 right-0 w-5 h-5 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-bl-lg rounded-tr-md hover:bg-red-600 transition-colors z-10"
      title={`${count} task${count > 1 ? "s" : ""} due`}
    >
      {count}
    </button>
  );
};

// Due Date Modal - Shows tasks due on a specific date
const DueDateModal = ({ date, tasks, onClose, canViewFinancials }) => {
  const [expandedTasks, setExpandedTasks] = useState({});

  if (!date || !tasks) return null;

  const formattedDate = new Date(date).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const toggleExpanded = (taskId) => {
    setExpandedTasks((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-start justify-center p-4 pt-16 overflow-y-auto"
      onClick={onClose}
    >
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1100]" />
      <div
        className="relative z-[1101] bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Tasks Due</h2>
              <p className="text-red-100 text-sm">{formattedDate}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tasks List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {tasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No tasks due on this date
            </div>
          ) : (
            tasks.map((task) => {
              const taskId = `${task.projectId}-${task.taskNo}`;
              const isOverdue = task.taskStatus === "Overdue";
              const isAttention = task.taskStatus === "Attention";
              const isExpanded = expandedTasks[taskId];

              return (
                <div
                  key={taskId}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 hover:border-gray-200 transition-all duration-200 overflow-hidden"
                >
                  {/* Collapsed Header */}
                  <div
                    onClick={() => toggleExpanded(taskId)}
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50/50 transition-colors"
                  >
                    {/* Status Indicator */}
                    <div
                      className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getStatusBgColor(
                        task.taskStatus
                      )} ${isOverdue || isAttention ? "animate-pulse" : ""}`}
                    />

                    {/* Task Number Badge */}
                    <span className="px-2 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-600 rounded">
                      #{task.taskNo}
                    </span>

                    {/* Task Type Badge */}
                    <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                      {task.task}
                    </span>

                    {/* Subject */}
                    <span className="text-sm text-gray-900 truncate flex-1 min-w-0 font-medium">
                      {task.subject}
                    </span>

                    {/* Country Flag */}
                    <CountryFlagIcon country={task.country} size={18} />

                    {/* Status Badge */}
                    <span
                      className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(
                        task.taskStatus
                      )}`}
                    >
                      {getStatusIcon(task.taskStatus)}
                      {task.taskStatus}
                    </span>

                    {/* Expand/Collapse Icon */}
                    <button
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-all flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(taskId);
                      }}
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {/* Project Reference (always visible) */}
                  <div className="px-3 pb-2 flex items-center gap-2 text-xs text-gray-500">
                    <Briefcase className="w-3 h-3" />
                    <span>{task.projectRef}</span>
                    <span className="text-gray-300">•</span>
                    <span>{task.client}</span>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50/50 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Project Info */}
                        <div className="space-y-2">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                            <Briefcase className="w-3.5 h-3.5" />
                            Project Info
                          </h4>
                          <div className="space-y-1.5 bg-white rounded-lg p-3 border border-gray-100">
                            <div className="flex justify-between items-center gap-2">
                              <span className="text-xs text-gray-500">
                                Client
                              </span>
                              <span className="text-sm font-medium text-gray-900 truncate max-w-[140px]">
                                {task.client || "N/A"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center gap-2">
                              <span className="text-xs text-gray-500">PoC</span>
                              <span className="text-sm font-medium text-gray-900 truncate max-w-[140px]">
                                {task.poc || "N/A"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center gap-2">
                              <span className="text-xs text-gray-500">
                                Country
                              </span>
                              <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                                <CountryFlagIcon
                                  country={task.country}
                                  size={16}
                                />
                                {task.country || "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Team & Schedule */}
                        <div className="space-y-2">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5" />
                            Team & Schedule
                          </h4>
                          <div className="space-y-1.5 bg-white rounded-lg p-3 border border-gray-100">
                            <div className="flex justify-between">
                              <span className="text-xs text-gray-500">
                                Analyst 1
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {task.analyst1 || "N/A"}
                              </span>
                            </div>
                            {task.analyst2 && (
                              <div className="flex justify-between">
                                <span className="text-xs text-gray-500">
                                  Analyst 2
                                </span>
                                <span className="text-sm font-medium text-gray-900">
                                  {task.analyst2}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-xs text-gray-500">
                                Due Date
                              </span>
                              <span
                                className={`text-sm font-medium ${
                                  isOverdue
                                    ? "text-red-600"
                                    : isAttention
                                    ? "text-amber-600"
                                    : "text-gray-900"
                                }`}
                              >
                                {formatDate(task.dueDate)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Completion */}
                        <div className="space-y-2">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Completion
                          </h4>
                          <div className="space-y-1.5 bg-white rounded-lg p-3 border border-gray-100">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-500">
                                Completed On
                              </span>
                              <span
                                className={`text-sm font-medium ${
                                  task.completedOn
                                    ? "text-green-600"
                                    : "text-gray-400"
                                }`}
                              >
                                {task.completedOn
                                  ? formatDate(task.completedOn)
                                  : "Not completed"}
                              </span>
                            </div>
                            {canViewFinancials && (
                              <>
                                <div className="border-t border-gray-100 pt-1.5 mt-1.5">
                                  <div className="flex justify-between">
                                    <span className="text-xs text-gray-500">
                                      Fee
                                    </span>
                                    <span className="text-sm font-medium text-gray-900">
                                      £{task.income || "0"}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-xs text-gray-500">
                                    Total Inc VAT
                                  </span>
                                  <span className="text-sm font-bold text-indigo-600">
                                    £{task.amountIncVat || "0"}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* View Project Button */}
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={() => {
                            window.open(
                              `/project/${task.projectId}`,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }}
                          className="px-4 py-1.5 text-xs font-semibold text-blue-600 bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-200 rounded-full hover:from-blue-100 hover:to-sky-100 hover:border-blue-300 hover:text-blue-700 flex items-center gap-1.5 transition-all shadow-sm hover:shadow"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          View Project
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 px-6 py-3 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            {tasks.length} task{tasks.length !== 1 ? "s" : ""} due
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Analyst Status Modal - Shows tasks for a specific analyst and status
const AnalystStatusModal = ({ analyst, status, tasks, onClose, canViewFinancials }) => {
  const [expandedTasks, setExpandedTasks] = useState({});

  if (!analyst || !status || !tasks) return null;

  const statusConfig = {
    "In Progress": {
      gradient: "from-blue-500 to-blue-600",
      lightText: "text-blue-100",
      icon: <Activity className="w-5 h-5 text-white" />,
    },
    "Attention": {
      gradient: "from-amber-500 to-amber-600",
      lightText: "text-amber-100",
      icon: <Clock className="w-5 h-5 text-white" />,
    },
    "Overdue": {
      gradient: "from-red-500 to-red-600",
      lightText: "text-red-100",
      icon: <AlertCircle className="w-5 h-5 text-white" />,
    },
  };

  const config = statusConfig[status] || statusConfig["In Progress"];

  const toggleExpanded = (taskId) => {
    setExpandedTasks((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-start justify-center p-4 pt-16 overflow-y-auto"
      onClick={onClose}
    >
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1100]" />
      <div
        className="relative z-[1101] bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`sticky top-0 z-10 bg-gradient-to-r ${config.gradient} px-6 py-4 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              {config.icon}
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{analyst}'s Tasks</h2>
              <p className={`${config.lightText} text-sm`}>{status}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tasks List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {tasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No {status.toLowerCase()} tasks for {analyst}
            </div>
          ) : (
            tasks.map((task) => {
              const taskId = `${task.projectId}-${task.taskNo}`;
              const isOverdue = task.taskStatus === "Overdue";
              const isAttention = task.taskStatus === "Attention";
              const isExpanded = expandedTasks[taskId];

              return (
                <div
                  key={taskId}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 hover:border-gray-200 transition-all duration-200 overflow-hidden"
                >
                  {/* Collapsed Header */}
                  <div
                    onClick={() => toggleExpanded(taskId)}
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50/50 transition-colors"
                  >
                    {/* Status Indicator */}
                    <div
                      className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getStatusBgColor(
                        task.taskStatus
                      )} ${isOverdue || isAttention ? "animate-pulse" : ""}`}
                    />

                    {/* Task Number Badge */}
                    <span className="px-2 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-600 rounded">
                      #{task.taskNo}
                    </span>

                    {/* Task Type Badge */}
                    <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                      {task.task}
                    </span>

                    {/* Subject */}
                    <span className="text-sm text-gray-900 truncate flex-1 min-w-0 font-medium">
                      {task.subject}
                    </span>

                    {/* Country Flag */}
                    <CountryFlagIcon country={task.country} size={18} />

                    {/* Status Badge */}
                    <span
                      className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(
                        task.taskStatus
                      )}`}
                    >
                      {getStatusIcon(task.taskStatus)}
                      {task.taskStatus}
                    </span>

                    {/* Expand/Collapse Icon */}
                    <button
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-all flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(taskId);
                      }}
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {/* Project Reference (always visible) */}
                  <div className="px-3 pb-2 flex items-center gap-2 text-xs text-gray-500">
                    <Briefcase className="w-3 h-3" />
                    <span>{task.projectRef}</span>
                    <span className="text-gray-300">•</span>
                    <span>{task.client}</span>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50/50 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Project Info */}
                        <div className="space-y-2">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                            <Briefcase className="w-3.5 h-3.5" />
                            Project Info
                          </h4>
                          <div className="space-y-1.5 bg-white rounded-lg p-3 border border-gray-100">
                            <div className="flex justify-between items-center gap-2">
                              <span className="text-xs text-gray-500">Client</span>
                              <span className="text-sm font-medium text-gray-900 truncate max-w-[140px]">
                                {task.client || "N/A"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center gap-2">
                              <span className="text-xs text-gray-500">PoC</span>
                              <span className="text-sm font-medium text-gray-900 truncate max-w-[140px]">
                                {task.poc || "N/A"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center gap-2">
                              <span className="text-xs text-gray-500">Country</span>
                              <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                                <CountryFlagIcon country={task.country} size={16} />
                                {task.country || "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Team & Schedule */}
                        <div className="space-y-2">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5" />
                            Team & Schedule
                          </h4>
                          <div className="space-y-1.5 bg-white rounded-lg p-3 border border-gray-100">
                            <div className="flex justify-between">
                              <span className="text-xs text-gray-500">Analyst 1</span>
                              <span className="text-sm font-medium text-gray-900">
                                {task.analyst1 || "N/A"}
                              </span>
                            </div>
                            {task.analyst2 && (
                              <div className="flex justify-between">
                                <span className="text-xs text-gray-500">Analyst 2</span>
                                <span className="text-sm font-medium text-gray-900">
                                  {task.analyst2}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-xs text-gray-500">Due Date</span>
                              <span
                                className={`text-sm font-medium ${
                                  isOverdue
                                    ? "text-red-600"
                                    : isAttention
                                    ? "text-amber-600"
                                    : "text-gray-900"
                                }`}
                              >
                                {formatDate(task.dueDate)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Completion */}
                        <div className="space-y-2">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Completion
                          </h4>
                          <div className="space-y-1.5 bg-white rounded-lg p-3 border border-gray-100">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-500">Completed On</span>
                              <span
                                className={`text-sm font-medium ${
                                  task.completedOn ? "text-green-600" : "text-gray-400"
                                }`}
                              >
                                {task.completedOn
                                  ? formatDate(task.completedOn)
                                  : "Not completed"}
                              </span>
                            </div>
                            {canViewFinancials && (
                              <>
                                <div className="border-t border-gray-100 pt-1.5 mt-1.5">
                                  <div className="flex justify-between">
                                    <span className="text-xs text-gray-500">Fee</span>
                                    <span className="text-sm font-medium text-gray-900">
                                      £{task.income || "0"}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-xs text-gray-500">Total Inc VAT</span>
                                  <span className="text-sm font-bold text-indigo-600">
                                    £{task.amountIncVat || "0"}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* View Project Button */}
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={() => {
                            window.open(
                              `/project/${task.projectId}`,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }}
                          className="px-4 py-1.5 text-xs font-semibold text-blue-600 bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-200 rounded-full hover:from-blue-100 hover:to-sky-100 hover:border-blue-300 hover:text-blue-700 flex items-center gap-1.5 transition-all shadow-sm hover:shadow"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          View Project
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 px-6 py-3 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            {tasks.length} task{tasks.length !== 1 ? "s" : ""}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Schedule Component
const Schedule = ({ user }) => {
  const { theme } = useTheme();
  const { can, loading: permsLoading, loadedFor } = usePermissions();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [dueDateModalDate, setDueDateModalDate] = useState(null);
  const [orphanedAllocationStatus, setOrphanedAllocationStatus] = useState({});
  const [viewMode, setViewMode] = useState("monthly"); // "monthly" or "today-centric"
  const [analystStatusModal, setAnalystStatusModal] = useState(null); // { analyst, status }

  const canViewSchedule = can(ACTIONS.NAV_VIEW_SCHEDULE);
  const canManageAllocations = can(ACTIONS.SCHEDULE_MANAGE_ALLOCATIONS);
  const canViewFinancials = can(ACTIONS.FINANCIALS_VIEW);
  const permsPending =
    permsLoading || (user?.email && loadedFor !== user.email.toLowerCase());

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const displayDays = useMemo(
    () =>
      viewMode === "today-centric"
        ? getTodayCentricDays()
        : getDaysInMonth(currentYear, currentMonth),
    [currentYear, currentMonth, viewMode]
  );

  const monthName = currentDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  // Build a task lookup map
  const tasksMap = useMemo(() => {
    const map = {};
    tasks.forEach((task) => {
      map[`${task.projectId}-${task.taskNo}`] = task;
    });
    return map;
  }, [tasks]);

  // Group tasks by due date for the badge
  const tasksDueByDate = useMemo(() => {
    const grouped = {};
    tasks.forEach((task) => {
      const dueDateKey = formatDateKey(task.dueDate);
      if (dueDateKey) {
        if (!grouped[dueDateKey]) {
          grouped[dueDateKey] = [];
        }
        grouped[dueDateKey].push(task);
      }
    });
    return grouped;
  }, [tasks]);

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Fetch tasks from projects
  useEffect(() => {
    if (permsPending) return;
    if (!canViewSchedule) {
      setTasks([]);
      return;
    }

    const fetchTasks = async () => {
      try {
        const projectsSnapshot = await getDocs(collection(db, "projects"));
        const allTasks = [];

        projectsSnapshot.forEach((docSnap) => {
          const projectData = docSnap.data();
          if (projectData.tasks && Array.isArray(projectData.tasks)) {
            const { updatedTasks } = evaluateTasks(projectData.tasks);

            updatedTasks.forEach((task) => {
              // Include all incomplete tasks
              if (task.taskStatus !== "Completed") {
                allTasks.push({
                  ...task,
                  projectRef: projectData.projectRef,
                  projectId: docSnap.id,
                  client: projectData.client,
                  poc: projectData.poc,
                });
              }
            });
          }
        });

        setTasks(allTasks);
      } catch (error) {
        console.error("Failed to fetch tasks:", error);
        setTasks([]);
      }
    };

    fetchTasks();
  }, [canViewSchedule, permsPending]);

  // Fetch allocations based on view mode
  useEffect(() => {
    if (permsPending) return;
    if (!canViewSchedule) {
      setAllocations([]);
      setLoading(false);
      return;
    }

    const fetchAllocations = async () => {
      setLoading(true);
      try {
        let startDate, endDate;

        if (viewMode === "today-centric") {
          // For today-centric view: (today - 3) to (today + 26)
          const today = new Date();
          const start = new Date(today);
          start.setDate(start.getDate() - 3);
          const end = new Date(today);
          end.setDate(end.getDate() + 26);
          startDate = formatDateKey(start);
          endDate = formatDateKey(end);
        } else {
          // Monthly view
          startDate = `${currentYear}-${String(currentMonth + 1).padStart(
            2,
            "0"
          )}-01`;
          endDate = `${currentYear}-${String(currentMonth + 1).padStart(
            2,
            "0"
          )}-31`;
        }

        const q = query(
          collection(db, "workAllocations"),
          where("date", ">=", startDate),
          where("date", "<=", endDate)
        );

        const snapshot = await getDocs(q);
        const allocs = [];
        snapshot.forEach((docSnap) => {
          allocs.push({ id: docSnap.id, ...docSnap.data() });
        });

        setAllocations(allocs);
      } catch (error) {
        console.error("Failed to fetch allocations:", error);
        setAllocations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllocations();
  }, [currentYear, currentMonth, canViewSchedule, permsPending, viewMode]);

  // Check orphaned allocations (allocations where task is not in tasksMap)
  useEffect(() => {
    const checkOrphanedAllocations = async () => {
      // Find allocations that don't have a corresponding task in tasksMap
      const orphaned = allocations.filter(
        (alloc) => !tasksMap[`${alloc.projectId}-${alloc.taskNo}`]
      );

      if (orphaned.length === 0) {
        setOrphanedAllocationStatus({});
        return;
      }

      const statusUpdates = {};

      for (const alloc of orphaned) {
        // Skip if we already checked this allocation AND it has complete data
        // (Re-check if status is "completed" but task data is missing - stale cache)
        const existingStatus = orphanedAllocationStatus[alloc.id];
        if (
          existingStatus &&
          !(existingStatus.status === "completed" && !existingStatus.task)
        )
          continue;

        try {
          const projectDoc = await getDoc(doc(db, "projects", alloc.projectId));

          if (!projectDoc.exists()) {
            // Project was deleted
            statusUpdates[alloc.id] = {
              status: "project_deleted",
              label: "Project Deleted",
              color: "bg-gray-400",
            };
          } else {
            const projectData = projectDoc.data();
            const projectTasks = projectData.tasks || [];
            const matchingTask = projectTasks.find(
              (t) => t.taskNo === alloc.taskNo
            );

            if (!matchingTask) {
              // Task was removed from project
              statusUpdates[alloc.id] = {
                status: "task_removed",
                label: "Task Removed",
                color: "bg-gray-400",
              };
            } else if (matchingTask.taskStatus === "Completed") {
              // Task is completed - store full task data for display
              statusUpdates[alloc.id] = {
                status: "completed",
                label: "Completed",
                color: "bg-green-500",
                task: {
                  ...matchingTask,
                  projectRef: projectData.projectRef,
                  projectId: alloc.projectId,
                  client: projectData.client,
                  poc: projectData.poc,
                },
              };
            } else {
              // Some other edge case (shouldn't normally happen)
              statusUpdates[alloc.id] = {
                status: "unknown",
                label: "Unavailable",
                color: "bg-gray-400",
              };
            }
          }
        } catch (error) {
          console.error("Error checking orphaned allocation:", error);
          statusUpdates[alloc.id] = {
            status: "error",
            label: "Error",
            color: "bg-gray-400",
          };
        }
      }

      if (Object.keys(statusUpdates).length > 0) {
        setOrphanedAllocationStatus((prev) => ({ ...prev, ...statusUpdates }));
      }
    };

    checkOrphanedAllocations();
  }, [allocations, tasksMap]);

  // Group allocations by analyst and date
  const allocationsByAnalystAndDate = useMemo(() => {
    const grouped = {};
    ANALYSTS.forEach((analyst) => {
      grouped[analyst] = {};
    });

    allocations.forEach((alloc) => {
      if (alloc.analyst && ANALYSTS.includes(alloc.analyst)) {
        if (!grouped[alloc.analyst][alloc.date]) {
          grouped[alloc.analyst][alloc.date] = [];
        }
        grouped[alloc.analyst][alloc.date].push(alloc);
      }
    });

    return grouped;
  }, [allocations]);

  // Count tasks by analyst and status (for header badges)
  const taskCountsByAnalyst = useMemo(() => {
    const counts = {};
    ANALYSTS.forEach((analyst) => {
      counts[analyst] = { inProgress: 0, attention: 0, overdue: 0 };
    });

    tasks.forEach((task) => {
      // Use Set to deduplicate (in case analyst1 === analyst2)
      const analysts = [...new Set([task.analyst1, task.analyst2].filter(Boolean))];
      analysts.forEach((analyst) => {
        if (counts[analyst]) {
          if (task.taskStatus === "In Progress") {
            counts[analyst].inProgress++;
          } else if (task.taskStatus === "Attention") {
            counts[analyst].attention++;
          } else if (task.taskStatus === "Overdue") {
            counts[analyst].overdue++;
          }
        }
      });
    });

    return counts;
  }, [tasks]);

  // Get tasks for a specific analyst and status (for the modal)
  const getTasksForAnalystStatus = useCallback((analyst, status) => {
    return tasks.filter((task) => {
      const isAssigned = task.analyst1 === analyst || task.analyst2 === analyst;
      return isAssigned && task.taskStatus === status;
    });
  }, [tasks]);

  // Filter and sort tasks for the pool
  const filteredTasks = useMemo(() => {
    let result = tasks;

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.projectRef?.toLowerCase().includes(q) ||
          t.subject?.toLowerCase().includes(q) ||
          t.client?.toLowerCase().includes(q) ||
          t.task?.toLowerCase().includes(q)
      );
    }

    // Sort by due date (soonest first, tasks without due date at the end)
    return [...result].sort((a, b) => {
      const dateA = a.dueDate ? new Date(a.dueDate) : null;
      const dateB = b.dueDate ? new Date(b.dueDate) : null;

      // Tasks without due date go to the end
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      return dateA - dateB;
    });
  }, [tasks, searchQuery]);

  // Navigation
  const goToPreviousMonth = () => {
    if (viewMode === "today-centric") {
      // Switch to monthly view showing the previous month from today
      const today = new Date();
      setCurrentDate(new Date(today.getFullYear(), today.getMonth() - 1, 1));
      setViewMode("monthly");
    } else {
      setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
    }
  };

  const goToNextMonth = () => {
    if (viewMode === "today-centric") {
      // Switch to monthly view showing the next month from today
      const today = new Date();
      setCurrentDate(new Date(today.getFullYear(), today.getMonth() + 1, 1));
      setViewMode("monthly");
    } else {
      setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setViewMode("today-centric");
  };

  // Add allocation
  const addAllocation = useCallback(
    async (task, analyst, date) => {
      if (!canManageAllocations) return;

      try {
        const newAlloc = {
          projectId: task.projectId,
          taskNo: task.taskNo,
          analyst,
          date,
          createdBy: user?.email || "unknown",
          createdAt: serverTimestamp(),
        };

        const docRef = await addDoc(
          collection(db, "workAllocations"),
          newAlloc
        );

        setAllocations((prev) => [...prev, { id: docRef.id, ...newAlloc }]);
      } catch (error) {
        console.error("Failed to add allocation:", error);
      }
    },
    [user?.email, canManageAllocations]
  );

  // Delete allocation
  const deleteAllocation = useCallback(
    async (allocationId) => {
      if (!canManageAllocations) return;

      try {
        await deleteDoc(doc(db, "workAllocations", allocationId));
        setAllocations((prev) => prev.filter((a) => a.id !== allocationId));
      } catch (error) {
        console.error("Failed to delete allocation:", error);
      }
    },
    [canManageAllocations]
  );

  // Drag handlers
  const handleDragStart = (event) => {
    if (!canManageAllocations) return;
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    if (!canManageAllocations) return;
    setOverId(event.over?.id || null);
  };

  const handleDragEnd = async (event) => {
    if (!canManageAllocations) return;

    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over) return;

    const activeData = active.data.current;
    const overId = over.id;

    // Parse the drop target (format: "cell-{analyst}-{date}")
    if (!overId.startsWith("cell-")) return;

    const [, analyst, date] = overId.split("-").reduce(
      (acc, part, idx) => {
        if (idx === 0) return acc;
        if (idx === 1) return [acc[0], part, acc[2]];
        return [acc[0], acc[1], acc[2] ? `${acc[2]}-${part}` : part];
      },
      ["", "", ""]
    );

    // Handle drop from task pool
    if (activeData.type === "task") {
      await addAllocation(activeData.task, analyst, date);
    }
    // Handle drop from another cell (move allocation)
    else if (activeData.type === "allocation") {
      const alloc = activeData.allocation;
      // Delete old, create new
      await deleteAllocation(alloc.id);
      await addAllocation(activeData.task, analyst, date);
    }
  };

  const handleDragCancel = () => {
    if (!canManageAllocations) return;
    setActiveId(null);
    setOverId(null);
  };

  // Get the active drag item for overlay
  const activeDragItem = useMemo(() => {
    if (!activeId) return null;

    if (activeId.startsWith("task-")) {
      const [, projectId, taskNo] = activeId.split("-");
      return tasksMap[`${projectId}-${taskNo}`];
    }

    if (activeId.startsWith("allocation-")) {
      const allocId = activeId.replace("allocation-", "");
      const alloc = allocations.find((a) => a.id === allocId);
      if (alloc) {
        return tasksMap[`${alloc.projectId}-${alloc.taskNo}`];
      }
    }

    return null;
  }, [activeId, tasksMap, allocations]);

  // Get the analysts to highlight during drag (assigned analysts for the dragged task)
  const highlightedAnalysts = useMemo(() => {
    if (!activeDragItem) return new Set();
    const analysts = new Set();
    if (activeDragItem.analyst1) analysts.add(activeDragItem.analyst1);
    if (activeDragItem.analyst2) analysts.add(activeDragItem.analyst2);
    return analysts;
  }, [activeDragItem]);

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  if (permsPending) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center space-y-2">
          <div className="mx-auto h-10 w-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }

  if (!canViewSchedule) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center space-y-2">
          <h1 className="text-xl font-semibold text-gray-900">
            Access restricted
          </h1>
          <p className="text-sm text-gray-600">
            You do not have permission to view the schedule.
          </p>
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={canManageAllocations ? sensors : []}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="min-h-screen bg-white pb-12 pt-6 font-sans">
        <HeaderActionsMinimal />
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div
            className="rounded-2xl shadow-lg border border-gray-200 p-6 mb-6 relative overflow-hidden"
            style={{ background: theme.loginBackground }}
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-20 translate-y-1/2 -translate-x-1/2"></div>

            <div className="relative z-10 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
              <div className="text-white">
                <div className="flex items-center gap-4 mb-2">
                  <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center backdrop-blur-sm border border-white/20">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold tracking-tight">
                      Work Allocation
                    </h1>
                    <p className="text-blue-100 text-base">
                      Drag tasks to allocate work days
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* View mode toggle */}
                <div className="flex items-center bg-white/10 rounded-lg border border-white/20 p-1">
                  <button
                    onClick={() => setViewMode("monthly")}
                    className={`p-1.5 rounded-md transition-colors ${
                      viewMode === "monthly"
                        ? "bg-white/20 text-white"
                        : "text-white/60 hover:text-white hover:bg-white/10"
                    }`}
                    title="Monthly view"
                  >
                    <CalendarDays className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("today-centric")}
                    className={`p-1.5 rounded-md transition-colors ${
                      viewMode === "today-centric"
                        ? "bg-white/20 text-white"
                        : "text-white/60 hover:text-white hover:bg-white/10"
                    }`}
                    title="Today-centric view (30 days)"
                  >
                    <Focus className="w-4 h-4" />
                  </button>
                </div>

                {/* Month navigation */}
                <button
                  onClick={goToPreviousMonth}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors border border-white/20"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>

                <div className="px-4 py-2 bg-white/10 rounded-lg border border-white/20 min-w-[180px] text-center">
                  <span className="text-white font-semibold">
                    {viewMode === "today-centric"
                      ? "30 Days Around Today"
                      : monthName}
                  </span>
                </div>

                <button
                  onClick={goToNextMonth}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors border border-white/20"
                >
                  <ChevronRight className="w-5 h-5 text-white" />
                </button>

                <button
                  onClick={goToToday}
                  className="ml-2 px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors border border-white/20 text-white text-sm font-medium"
                >
                  Today
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex gap-6">
            {/* Task Pool Sidebar */}
            <div className="w-72 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Task Pool
                  </h3>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search tasks..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div
                  className="p-3 space-y-2 overflow-y-auto overflow-x-hidden"
                  style={{ maxHeight: "calc(100vh - 300px)" }}
                >
                  {filteredTasks.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No tasks found
                    </p>
                  ) : (
                    filteredTasks.map((task) => (
                      <DraggableTaskPill
                        key={`${task.projectId}-${task.taskNo}`}
                        task={task}
                        onClick={setSelectedTask}
                        draggable={canManageAllocations}
                      />
                    ))
                  )}
                </div>
                <div className="p-3 border-t border-gray-100 bg-gray-50">
                  <p className="text-xs text-gray-500 text-center">
                    {filteredTasks.length} tasks available
                  </p>
                </div>
              </div>
            </div>

            {/* Schedule Grid */}
            <div className="flex-1 min-w-0">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {loading ? (
                  <div className="p-12 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-500">Loading allocations...</p>
                  </div>
                ) : (
                  <div className="overflow-auto max-h-[calc(100vh-280px)]">
                    <table className="w-full border-collapse">
                      <thead className="sticky top-0 z-30">
                        <tr>
                          <th className="sticky left-0 top-0 z-40 bg-gray-50 border-b border-r border-gray-200 px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                            Date
                          </th>
                          {ANALYSTS.map((analyst) => {
                            const counts = taskCountsByAnalyst[analyst] || {};
                            const hasBadges =
                              counts.inProgress > 0 ||
                              counts.attention > 0 ||
                              counts.overdue > 0;
                            const isHighlighted = highlightedAnalysts.has(analyst);
                            return (
                              <th
                                key={analyst}
                                className={`sticky top-0 border-b border-r border-gray-200 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider transition-colors duration-200 ${
                                  isHighlighted
                                    ? "bg-indigo-100 text-indigo-800 border-indigo-200"
                                    : "bg-gray-50 text-gray-700"
                                }`}
                              >
                                <div className="flex flex-col items-center gap-1">
                                  <span>{analyst}</span>
                                  {hasBadges && (
                                    <div className="flex items-center justify-center gap-2">
                                      {counts.inProgress > 0 && (
                                        <button
                                          onClick={() => setAnalystStatusModal({ analyst, status: "In Progress" })}
                                          className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                                          title={`${counts.inProgress} In Progress - Click to view`}
                                        >
                                          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                          {counts.inProgress}
                                        </button>
                                      )}
                                      {counts.attention > 0 && (
                                        <button
                                          onClick={() => setAnalystStatusModal({ analyst, status: "Attention" })}
                                          className="flex items-center gap-1 text-[10px] text-amber-600 hover:text-amber-800 transition-colors cursor-pointer"
                                          title={`${counts.attention} Attention - Click to view`}
                                        >
                                          <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                                          {counts.attention}
                                        </button>
                                      )}
                                      {counts.overdue > 0 && (
                                        <button
                                          onClick={() => setAnalystStatusModal({ analyst, status: "Overdue" })}
                                          className="flex items-center gap-1 text-[10px] text-red-600 hover:text-red-800 transition-colors cursor-pointer"
                                          title={`${counts.overdue} Overdue - Click to view`}
                                        >
                                          <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                          {counts.overdue}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {displayDays.map((date) => {
                          const dateKey = formatDateKey(date);
                          const todayHighlight = isToday(date);
                          const weekend = isWeekend(date);
                          const dayName = date.toLocaleDateString("en-US", {
                            weekday: "short",
                          });
                          const dayNum = date.getDate();
                          const tasksDueCount =
                            tasksDueByDate[dateKey]?.length || 0;

                          return (
                            <tr
                              key={dateKey}
                              className={
                                todayHighlight
                                  ? "bg-indigo-50"
                                  : weekend
                                  ? "bg-gray-50/50"
                                  : ""
                              }
                            >
                              <td
                                className={`relative sticky left-0 z-10 border-b border-r border-gray-200 px-3 py-2 ${
                                  todayHighlight
                                    ? "bg-indigo-50"
                                    : weekend
                                    ? "bg-gray-50"
                                    : "bg-white"
                                }`}
                              >
                                <DueDateBadge
                                  count={tasksDueCount}
                                  onClick={() => setDueDateModalDate(dateKey)}
                                />
                                <div className="flex flex-col">
                                  <span
                                    className={`text-sm font-semibold ${
                                      todayHighlight
                                        ? "text-indigo-600"
                                        : weekend
                                        ? "text-gray-400"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    {dayNum}
                                  </span>
                                  <span
                                    className={`text-xs ${
                                      todayHighlight
                                        ? "text-indigo-500"
                                        : weekend
                                        ? "text-gray-400"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {dayName}
                                  </span>
                                </div>
                              </td>

                              {ANALYSTS.map((analyst) => {
                                const cellId = `cell-${analyst}-${dateKey}`;
                                const cellAllocations =
                                  allocationsByAnalystAndDate[analyst]?.[
                                    dateKey
                                  ] || [];
                                const isOverThis = overId === cellId;
                                const isColumnHighlighted = highlightedAnalysts.has(analyst);

                                return (
                                  <DroppableCell
                                    key={cellId}
                                    id={cellId}
                                    isOver={isOverThis}
                                    disabled={!canManageAllocations}
                                    isHighlighted={isColumnHighlighted}
                                  >
                                    <div className="flex flex-col gap-1 min-h-[50px]">
                                      {cellAllocations.map((alloc) => {
                                        const task =
                                          tasksMap[
                                            `${alloc.projectId}-${alloc.taskNo}`
                                          ];

                                        // If task is not found, show orphaned allocation pill
                                        if (!task) {
                                          return (
                                            <OrphanedAllocationPill
                                              key={alloc.id}
                                              allocation={alloc}
                                              statusInfo={
                                                orphanedAllocationStatus[
                                                  alloc.id
                                                ]
                                              }
                                              onDelete={deleteAllocation}
                                              onClick={setSelectedTask}
                                              canManage={canManageAllocations}
                                            />
                                          );
                                        }

                                        return (
                                          <DraggableAllocationPill
                                            key={alloc.id}
                                            allocation={alloc}
                                            task={task}
                                            onDelete={deleteAllocation}
                                            onClick={setSelectedTask}
                                            canManage={canManageAllocations}
                                          />
                                        );
                                      })}
                                    </div>
                                  </DroppableCell>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Legend */}
              <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex flex-wrap items-center gap-6">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Status:
                  </h3>
                  {[
                    { status: "In Progress", color: "bg-blue-500" },
                    { status: "Attention", color: "bg-amber-500" },
                    { status: "Overdue", color: "bg-red-500" },
                  ].map(({ status, color }) => (
                    <div key={status} className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded ${color}`}></div>
                      <span className="text-sm text-gray-600">{status}</span>
                    </div>
                  ))}
                  <div className="ml-auto text-xs text-gray-400">
                    Drag tasks from the pool to allocate work days
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Task Preview Modal */}
        {selectedTask && (
          <TaskPreviewModal
            task={selectedTask}
            onClose={() => setSelectedTask(null)}
            onViewProject={() => {
              if (selectedTask?.projectId) {
                window.open(
                  `/project/${selectedTask.projectId}`,
                  "_blank",
                  "noopener,noreferrer"
                );
              }
            }}
            canViewFinancials={canViewFinancials}
            theme={theme}
          />
        )}

        {/* Due Date Modal */}
        {dueDateModalDate && (
          <DueDateModal
            date={dueDateModalDate}
            tasks={tasksDueByDate[dueDateModalDate] || []}
            onClose={() => setDueDateModalDate(null)}
            canViewFinancials={canViewFinancials}
          />
        )}

        {/* Analyst Status Modal */}
        {analystStatusModal && (
          <AnalystStatusModal
            analyst={analystStatusModal.analyst}
            status={analystStatusModal.status}
            tasks={getTasksForAnalystStatus(analystStatusModal.analyst, analystStatusModal.status)}
            onClose={() => setAnalystStatusModal(null)}
            canViewFinancials={canViewFinancials}
          />
        )}

        {/* Drag Overlay */}
        <DragOverlay dropAnimation={null}>
          {activeDragItem && (
            <div
              className={`${getStatusBgColor(
                activeDragItem.taskStatus
              )} text-white text-xs px-3 py-2 rounded shadow-lg opacity-90`}
            >
              {activeDragItem.projectRef} ({activeDragItem.task})
            </div>
          )}
        </DragOverlay>
      </div>
    </DndContext>
  );
};

export default Schedule;
